import { formatMoney } from './format'

/**
 * @param {import('./types').InsightRecord} insight
 * @returns {import('./types').RecommendationCardDraft[]}
 */
export function buildWeekdayUnderperformanceCards(insight) {
  const metadata =
    typeof insight.metadata === 'string'
      ? JSON.parse(insight.metadata || '{}')
      : insight.metadata || {}

  const dayName = metadata.dayName || 'Weekday'
  const rawGapPct = Number(metadata.revenueGapPct ?? 0)
  const gapPct =
    Math.abs(rawGapPct) <= 1 ? Math.abs(rawGapPct * 100) : Math.abs(rawGapPct)
  const impact = Number(insight.impact_amount || 0)
  const confidence = Number(insight.confidence_score || 0)
  const priority = insight.severity || 'medium'

  const gapSubtitle =
    gapPct > 0
      ? `${dayName} games are underperforming by ${gapPct.toFixed(1)}%.`
      : `${dayName} games are underperforming.`

  return [
    {
      category: 'promotion',
      priority,
      title: `Launch ${dayName} Family 4-Pack`,
      subtitle: `${gapSubtitle} Bundle tickets, food, and fees into a simple family offer.`,
      expectedRevenueLift: impact * 0.35,
      confidenceScore: confidence,
      primaryMetric: {
        label: 'Expected Lift',
        value: formatMoney(impact * 0.35, { signed: true }),
      },
      secondaryMetric: {
        label: 'Revenue Gap',
        value: formatMoney(impact, { signed: true }),
      },
      suggestedActions: [
        {
          type: 'create_offer',
          label: 'Create family 4-pack offer',
          actionPayload: {
            actionType: 'create_offer',
            offerType: 'family_4_pack',
            dayOfWeek: metadata.dayOfWeek,
            suggestedName: `${dayName} Family 4-Pack`,
          },
        },
      ],
      ctaLabel: 'Create Offer',
      actionPayload: {
        actionType: 'create_offer',
        offerType: 'family_4_pack',
        dayOfWeek: metadata.dayOfWeek,
        suggestedName: `${dayName} Family 4-Pack`,
      },
      displayOrder: 1,
    },
    {
      category: 'marketing',
      priority: 'medium',
      title: `Email Previous ${dayName} Buyers`,
      subtitle: `Target customers who have purchased or attended ${dayName} games before.`,
      expectedRevenueLift: impact * 0.25,
      confidenceScore: Math.max(confidence - 5, 50),
      primaryMetric: {
        label: 'Expected Lift',
        value: formatMoney(impact * 0.25, { signed: true }),
      },
      secondaryMetric: {
        label: 'Audience',
        value: 'Previous weekday buyers',
      },
      suggestedActions: [
        {
          type: 'create_audience',
          label: 'Build weekday buyer audience',
          actionPayload: {
            actionType: 'create_audience',
            audienceType: 'previous_weekday_buyers',
            dayOfWeek: metadata.dayOfWeek,
          },
        },
      ],
      ctaLabel: 'Build Audience',
      actionPayload: {
        actionType: 'create_audience',
        audienceType: 'previous_weekday_buyers',
        dayOfWeek: metadata.dayOfWeek,
      },
      displayOrder: 2,
    },
    {
      category: 'pricing',
      priority: 'medium',
      title: 'Discount Low-Demand Sections',
      subtitle:
        'Use a targeted discount on sections with high remaining inventory.',
      expectedRevenueLift: impact * 0.25,
      confidenceScore: Math.max(confidence - 8, 50),
      primaryMetric: {
        label: 'Expected Lift',
        value: formatMoney(impact * 0.25, { signed: true }),
      },
      secondaryMetric: {
        label: 'Target',
        value: 'High inventory sections',
      },
      suggestedActions: [
        {
          type: 'review_pricing',
          label: 'Review section pricing',
          actionPayload: {
            actionType: 'review_pricing',
            pricingStrategy: 'low_demand_sections',
          },
        },
      ],
      ctaLabel: 'Review Pricing',
      actionPayload: {
        actionType: 'review_pricing',
        pricingStrategy: 'low_demand_sections',
      },
      displayOrder: 3,
    },
    {
      category: 'operations',
      priority: 'low',
      title: `Test Later ${dayName} Start Time`,
      subtitle:
        'Later weekday start times may improve attendance for families and after-work buyers.',
      expectedRevenueLift: impact * 0.15,
      confidenceScore: Math.max(confidence - 15, 40),
      primaryMetric: {
        label: 'Expected Lift',
        value: formatMoney(impact * 0.15, { signed: true }),
      },
      secondaryMetric: {
        label: 'Experiment',
        value: 'Start time adjustment',
      },
      suggestedActions: [
        {
          type: 'simulate_start_time',
          label: 'Simulate start time change',
          actionPayload: {
            actionType: 'simulate_start_time',
            adjustmentMinutes: 30,
          },
        },
      ],
      ctaLabel: 'Simulate Change',
      actionPayload: {
        actionType: 'simulate_start_time',
        adjustmentMinutes: 30,
      },
      displayOrder: 4,
    },
  ]
}
